import React, { useState } from "react";
import { Instagram } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PatternOverlay from "@/components/PatternOverlay";

const INSTAGRAM_URL =
  "https://www.instagram.com/aexents?igsh=MTB2ZG9send0Nm1keA%3D%3D&utm_source=qr";

const HERO_IMG = "/coming-soon-hero.jpg";

export default function ComingSoonPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    setMessage("");
    try {
      await api.post("/subscribe", { email, source: "coming_soon" });
      setStatus("success");
      setMessage("Thank you. You're on the list — we'll be in touch.");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="pattern-bg min-h-screen relative overflow-hidden" data-testid="coming-soon-page">
      {/* Backdrop image */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background/95" />
      </div>

      <PatternOverlay opacity={0.10} size={520} />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top bar: brand only */}
        <header className="w-full px-6 md:px-12 pt-10 md:pt-14 flex justify-center">
          <img
            src="/logo-aexents.png"
            alt="æxènts London"
            className="h-20 md:h-28 w-auto animate-fade-up"
          />
        </header>

        {/* Centered content */}
        <main className="flex-1 flex items-center justify-center px-6 md:px-12 py-16">
          <div className="max-w-2xl w-full text-center">
            <div
              className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.4em] text-primary whitespace-nowrap animate-fade-up"
              style={{ animationDelay: "120ms" }}
            >
              A modern perfumery · Arriving soon
            </div>

            <h1
              className="font-serif text-6xl md:text-8xl leading-[0.95] tracking-tight mt-8 animate-fade-up"
              style={{ animationDelay: "240ms" }}
            >
              Scent as
              <br />
              <span className="block font-script not-italic text-primary text-7xl md:text-9xl leading-none -translate-x-2 md:-translate-x-4">
                Signature
              </span>
            </h1>

            <p
              className="mt-8 text-base md:text-lg leading-relaxed text-muted-foreground max-w-lg mx-auto animate-fade-up"
              style={{ animationDelay: "360ms" }}
            >
              Rare essences, cinematic compositions and harmonious
              layering—handcrafted in London. Enter your email to be the first
              to know when the house opens its doors.
            </p>

            <form
              onSubmit={submit}
              className="mt-12 flex flex-col sm:flex-row items-stretch gap-3 max-w-md mx-auto animate-fade-up"
              style={{ animationDelay: "480ms" }}
              data-testid="coming-soon-form"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yours.com"
                data-testid="coming-soon-email"
                aria-label="Email address"
                className="flex-1 bg-transparent border-b border-border px-0 py-3 text-foreground focus:outline-none focus:border-primary text-sm placeholder:text-muted-foreground text-center sm:text-left"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="coming-soon-submit"
                className="bg-primary text-primary-foreground uppercase tracking-[0.22em] text-xs px-8 py-4 hover:opacity-90 transition-opacity disabled:opacity-60 whitespace-nowrap"
              >
                {status === "loading" ? "Adding…" : "Notify Me"}
              </button>
            </form>

            {message && (
              <div
                data-testid="coming-soon-message"
                className={`mt-6 text-sm animate-fade-up ${
                  status === "success" ? "text-primary" : "text-destructive"
                }`}
              >
                {message}
              </div>
            )}

            {/* Follow us */}
            <div
              className="mt-14 flex flex-col items-center gap-3 animate-fade-up"
              style={{ animationDelay: "560ms" }}
            >
              <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">
                Follow the journey
              </div>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="coming-soon-instagram"
                className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.28em] text-muted-foreground hover:text-primary transition-colors"
                aria-label="Follow æxènts on Instagram"
              >
                <Instagram size={16} className="transition-transform duration-500 group-hover:scale-110" />
                <span>@aexents</span>
              </a>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer
          className="w-full px-6 md:px-12 pb-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs uppercase tracking-[0.28em] text-muted-foreground animate-fade-up"
          style={{ animationDelay: "600ms" }}
        >
          <div>© {new Date().getFullYear()} æxènts London</div>
          <div>aexents.com</div>
        </footer>
      </div>
    </div>
  );
}
